import { ConsoleRouteMap } from "apps/console/routes/routes";
import React from "react";
import LoggedInUserType from "shared/enums/LoggedInUserType";
import { useGetAuthorities } from "./dataloader";
import {
  ApiLoggedInUser,
  AuthorityConfig,
  AuthorityContext,
  AuthorityProviderProps,
  ConsoleAuthorityInput,
  ConsoleMultipleAuthorities
} from "./types";
import isEmpty from "lodash/isEmpty";
import intersection from "lodash/intersection";
import Authority from "shared/enums/Authority";
import { setLocaleId } from "shared/utils";
import { useStaticData } from "context/staticData";

export const Context = React.createContext<AuthorityContext>({} as AuthorityContext);

export function processApiForConsole(
  data: ApiLoggedInUser,
  contextId?: string
): { authorities: Array<string>; config: AuthorityConfig } {
  let authorities: Array<string> = [];
  if (data.platformUser) {
    authorities = data.consoleUserRoles.find(item => item.contextId === data.platformId)?.authorities || [];
  } else {
    if (contextId) {
      authorities = data.consoleUserRoles.find(item => item.contextId === contextId)?.authorities || [];
    }
  }

  return {
    authorities,
    config: {
      userType: data.platformUser ? LoggedInUserType.PLATFORM : LoggedInUserType.EVENT_SERIES,
      homePage: data.platformUser
        ? `/${ConsoleRouteMap.HOME.clientList.path}`
        : `${ConsoleRouteMap.CLIENT.path}/${ConsoleRouteMap.CLIENT.eventSeriesList.path}`
    }
  };
}

export function processApiForMyExperience(data: ApiLoggedInUser, contextId?: string) {
  let authorities: Array<string> = [];
  return {
    authorities,
    userType: LoggedInUserType.MY_EXP
  };
}

function AuthorityProvider(props: AuthorityProviderProps) {
  const { languageTypeLocaleId } = useStaticData();

  const updateLocale = React.useCallback(
    (data: ApiLoggedInUser) => {
      setLocaleId(languageTypeLocaleId[data?.languageType]);
    },
    [languageTypeLocaleId]
  );

  const { data } = useGetAuthorities({ successHandler: updateLocale });
  const { contextId, processApiData } = props;

  const authority = React.useMemo(() => {
    let authorities: Array<string> = [];
    let config: AuthorityConfig = { userType: "", homePage: "/" };
    if (data) {
      let processedData = processApiData(data, contextId);
      authorities = processedData.authorities;
      config = processedData.config;
    }

    function hasAuthority(authority: string) {
      return !!authorities.includes(authority);
    }

    function hasAllAuthorities(authoritiesListToCheckWith: Array<Authority>) {
      return intersection(authoritiesListToCheckWith, authorities).length === authoritiesListToCheckWith.length;
    }

    function hasAnyAuthority(authoritiesListToCheckWith: Array<Authority>) {
      return intersection(authoritiesListToCheckWith, authorities).length > 0;
    }

    function hasProgramWriteAuthority() {
      return hasAuthority(Authority.PROGRAM_WRITE);
    }

    function hasProgramReadAuthority() {
      return hasAuthority(Authority.PROGRAM_READ);
    }

    function hasPlatformConfigWriteAuthority() {
      return hasAuthority(Authority.PLATFORM_CONFIG_WRITE);
    }

    function hasEventReadAuthority() {
      return hasAuthority(Authority.EVENT_READ);
    }

    function hasEventWriteAuthority() {
      return hasAuthority(Authority.EVENT_WRITE);
    }

    function hasRegistrationCreateAuthority() {
      return hasAuthority(Authority.REGISTRATION_CREATE);
    }

    function hasRegistrationWriteAuthority() {
      return hasAuthority(Authority.REGISTRATION_WRITE);
    }

    function hasPlatformClientWriteAuthority() {
      return hasAuthority(Authority.PLATFORM_CLIENT_WRITE);
    }

    function hasPlatformUserWriteAuthority() {
      return hasAuthority(Authority.PLATFORM_USER_WRITE);
    }

    function hasEventSeriesUserWrite() {
      return hasAuthority(Authority.EVENT_SERIES_USER_WRITE);
    }

    function hasPlatformConfigReadAuthority() {
      return hasAuthority(Authority.PLATFORM_CONFIG_READ);
    }

    function hasEventSeriesWriteAuthority() {
      return hasAuthority(Authority.EVENT_SERIES_WRITE);
    }

    return {
      authorities,
      hasAuthority,
      config,
      hasAllAuthorities,
      hasAnyAuthority,
      hasProgramWriteAuthority,
      hasProgramReadAuthority,
      hasPlatformConfigWriteAuthority,
      hasEventReadAuthority,
      hasEventWriteAuthority,
      hasRegistrationCreateAuthority,
      hasRegistrationWriteAuthority,
      hasPlatformClientWriteAuthority,
      hasPlatformUserWriteAuthority,
      hasEventSeriesUserWrite,
      hasPlatformConfigReadAuthority,
      hasEventSeriesWriteAuthority
    };
  }, [data, processApiData, contextId]);

  if (!data) {
    return null;
  }

  return <Context.Provider value={authority} {...props} />;
}

function useAuthority(): AuthorityContext {
  const context = React.useContext(Context);

  if (isEmpty(context)) {
    throw new Error(`useConsoleAuthority must be used within a AuthorityProvider`);
  }
  return context;
}

// todo:rr discuss with RD and remove memo
// const HasAuthority = React.memo(
//   function (props: ConsoleAuthorityInput) {
//     const { hasAuthority } = useAuthority();
//     return hasAuthority(props.authority) && props.children ? props.children : null;
//   },
//   (prevProps, nextProps) => prevProps.authority === nextProps.authority
// );

function HasAuthority(props: ConsoleAuthorityInput) {
  const { hasAuthority } = useAuthority();
  return hasAuthority(props.authority) && props.children ? props.children : null;
}

const HasAllAuthorities = React.memo(
  (props: ConsoleMultipleAuthorities) => {
    const { hasAllAuthorities } = useAuthority();
    return hasAllAuthorities(props.authorities) && props.children ? props.children : null;
  },
  (prevProps, nextProps) => prevProps.authorities === nextProps.authorities // todo:rr
);

const HasAnyAuthority = React.memo(
  (props: ConsoleMultipleAuthorities) => {
    const { hasAnyAuthority } = useAuthority();
    return hasAnyAuthority(props.authorities) && props.children ? props.children : null;
  },
  (prevProps, nextProps) => prevProps.authorities === nextProps.authorities // todo:rr
);

export { AuthorityProvider, useAuthority, HasAuthority, HasAllAuthorities, HasAnyAuthority };
